import type { AppField, ID } from "@smartsuite/smartsuite-client";
import type { ActivityRecord } from "../../../stores/jobs.store";
import type { HeadCellComponent } from "@smartsuite/grid/lib/components/GridHead/GridHead";
import type { GridSettings } from "@smartsuite/grid/lib/Grid.types";
import type {
  ColumnsWidths,
  GridContentAlignment,
  GridHeadAlignment,
} from "@smartsuite/grid/lib/types";
import { memo, useCallback, useMemo } from "react";
import type { ReactElement } from "react";

import type { RowRendererProps } from "@smartsuite/grid/lib/components/GridRowList/GridRowList.types";
import { Grid } from "@smartsuite/grid/lib/Grid";

import type { UserPreferences } from "../../../types/user-preferences";
import {
  automationActivityGridScrollOptions,
  automationActivityGridSettings,
} from "./ActivityGrid.contants";
import { filterFieldsBySlugList } from "./ActivityGrid.helpers";

import type { ScrollPosition } from "@smartsuite/grid/lib/hooks/useScrollPosition";
import "@smartsuite/grid/lib/styles.sass";
import { ActivityGridCell } from "./components/ActivityGridCell/ActivityGridCell";
import { ActivityGridHeadCell } from "./components/ActivityGridHeadCell/ActivityGridHeadCell";
import { activityGridHeader } from "./components/ActivityGridHeader/ActivityGridHeader";
import { ActivityGridRow } from "./components/ActivityGridRow/ActivityGridRow";
import { HistoryLoader } from "./components/HistoryLoader/HistoryLoader";
import { NoRecords } from "./components/NoRecords/NoRecords";
import { StyledGrid } from "./style";
import { useRootStore } from "../../../stores/hooks";
import { observer } from "mobx-react-lite";

interface ColumnSettings {
  // A label to be shown in a grid column head
  icon: string;
  // Specifies whether to show field label in a grid column head
  showLabel: boolean;
  // Min grid column width
  minWidth: number;
  // Default grid column width
  defaultWidth: number;
  // determines whether column width can be changed
  resizable: boolean;
  // content align of field
  contentAlignment?: GridContentAlignment;
  // header align of field
  headerAlignment?: GridHeadAlignment;
}

interface ActivityGridProps {
  isLoading: boolean;
  headerHeight: number;
  structure: AppField[];
  visibleFields: string[];
  columnWidths?: ColumnsWidths;
  userPreferences: UserPreferences;
  onScroll: (
    position: ScrollPosition,
    scrollContentSize: {
      height: number;
      width: number;
    },
  ) => void;
  onDetails: (id: ID) => void;
}

const ActivityGridRaw = observer<ActivityGridProps>(
  ({
    isLoading,
    headerHeight,
    structure,
    columnWidths,
    userPreferences,
    visibleFields,
    onScroll,
    onDetails,
  }: ActivityGridProps): ReactElement => {
    const { jobsStore } = useRootStore();
    const fields = useMemo(() => {
      return filterFieldsBySlugList(structure, visibleFields);
    }, [structure, visibleFields]);

    const headCellRenderer: HeadCellComponent<AppField, GridSettings> = ({
      column,
      ...props
    }) => {
      return <ActivityGridHeadCell {...props} field={column} />;
    };

    const rowRenderer = useCallback(
      (
        props: RowRendererProps<ActivityRecord, AppField, GridSettings>,
      ): ReactElement => {
        const { columns, ...restProps } = props;

        return (
          <ActivityGridRow
            fields={columns}
            onDetails={onDetails}
            {...restProps}
          />
        );
      },
      [jobsStore.jobs],
    );

    const cellRenderer = useCallback(
      ({ record, column, ...props }): ReactElement => {
        return (
          <ActivityGridCell
            {...props}
            record={record}
            field={column}
            userPreferences={userPreferences}
          />
        );
      },
      [userPreferences],
    );

    return (
      <StyledGrid headerHeight={headerHeight}>
        <Grid
          scrollOptions={automationActivityGridScrollOptions}
          settings={automationActivityGridSettings}
          records={jobsStore.jobs}
          columns={fields}
          rowSize={34}
          getColumnSettings={getColumnSettings}
          columnWidths={columnWidths}
          // Grid's exported type pins TRecord to { id: ID }; getRecordKey is
          // scoped to our synthetic ActivityRecord rows (id: string), same shape.
          getRecordKey={getRecordKey as (record: { id: ID }) => string}
          // @ts-expect-error
          getColumnKey={getColumnKey}
          // @ts-expect-error
          headCellComponent={headCellRenderer}
          cellComponent={cellRenderer}
          // @ts-expect-error
          headerComponent={activityGridHeader}
          // @ts-expect-error
          rowComponent={rowRenderer}
          onScroll={onScroll}
        >
          {isLoading && jobsStore.jobs.length === 0 && <HistoryLoader />}
          {!isLoading && jobsStore.jobs.length === 0 && <NoRecords />}
        </Grid>
      </StyledGrid>
    );
  },
);

export const ActivityGrid = memo(ActivityGridRaw);

// Tolerate a missing record: @smartsuite/grid (>=5.22) calls getRecordKey
// with `undefined` while no drag is active (useRecordDragging ->
// canDropIntoEveryGroupLevel runs on mount with sourceRecord=undefined);
// the "" key safely misses every position/group lookup.
const getRecordKey = <TRecord extends ActivityRecord>(
  record: TRecord | undefined,
): string => record?.id ?? "";
const getColumnKey = (column: AppField): string => column.slug;
const getColumnSettings = (): ColumnSettings => ({
  icon: "",
  showLabel: false,
  minWidth: 72,
  defaultWidth: 200,
  resizable: true,
});
